import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDevice } from "../../../hooks/useDevices";
import LocationSelector from "../../formComponents/locationSelector";
import Modal, { MODAL_SIZES } from "../modal/Modal";
import { useDeviceSettingsContext } from "../../../context/DeviceSettingsContext";
import DevicePhotos, { FileStateType } from "../../photos";
import { nanoid } from "nanoid";
import ConfirmationDialog from "../ConfirmationDialog";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import WateringNeedsSlider from "../../ui/WateringNeedsSlider";

type EditDeviceModalProps = {
  onClose: () => void;
};

const EditDeviceModal = ({ onClose }: EditDeviceModalProps) => {
  const { selectedDevice, onCloseDeviceModal } = useDeviceSettingsContext();
  const [formError, setFormError] = useState<string | null>(null);
  const { device, isLoading, updateDevice, savePhotos, deleteDevice, updateWateringNeeds } = useDevice(
    selectedDevice.device_id
  );
  const { register, handleSubmit, control, setValue, formState: { isDirty }, reset, watch } = useForm();
  const [photosChanged, setPhotosChanged] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingLocationNavigation, setPendingLocationNavigation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const [photos, setPhotos] = useState<FileStateType[]>(selectedDevice.photos?.map(p => {
    if (typeof p === 'object' && p.created_at) {
      return {
        id: nanoid(),
        type: "server" as const,
        url: p.url,
        addedAt: new Date(p.created_at)
      };
    } else {
      // Extract timestamp from URL filename (e.g., 1752178253829-blob)
      const url = typeof p === 'string' ? p : p.url;
      const timestampMatch = url.match(/\/(\d{13})-blob$/);
      let addedAt: Date | undefined = undefined;
      
      if (timestampMatch) {
        const timestamp = parseInt(timestampMatch[1]);
        addedAt = new Date(timestamp);
      }
      
      return {
        id: nanoid(),
        type: "server" as const,
        url: url,
        addedAt: addedAt
      };
    }
  }) || [])

  const handlePhotosChange = (newPhotos: React.SetStateAction<FileStateType[]>) => {
    setPhotos(newPhotos);
    setPhotosChanged(true);
  };

  const hasUnsavedChanges = isDirty || photosChanged;

  const closeModal = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onCloseDeviceModal(); // call the context handler
      onClose(); // call the component handler
    }
  };

  const forceCloseModal = () => {
    onCloseDeviceModal(); // call the context handler
    onClose(); // call the component handler
  };

  useEffect(() => {
    if (device) {
      const formData = {
        name: device.name,
        location_id: device.location_id,
        notes: device.notes,
        color: device.color,
        waterIntSet: device.nuf_watering_need_set === -1 ? 5 : (device.nuf_watering_need_set || 5)
      };
      reset(formData); // Use reset instead of setValue to properly initialize form state
    }
  }, [device, reset]);

  const onSubmit = async (data: any) => {
    setIsSaving(true);
    try {
      console.log("🔄 Starting device update...");
      
      // Extract waterIntSet from data and include in update if provided
      const { waterIntSet, ...deviceData } = data;
      
      // Prepare update data including watering needs if provided
      const updateData = {
        ...deviceData,
        ...(waterIntSet !== undefined ? { nuf_watering_need_set: Number(waterIntSet) } : {})
      };
      
      console.log("🔄 Updating device with data:", updateData);
      // Single API call to update all device data including watering needs
      await updateDevice(updateData);
      console.log("✅ Device updated successfully");

      console.log("🔄 Saving photos...");
      await savePhotos(photos);
      console.log("✅ Photos saved successfully");

      toast.success("Device updated successfully");
      forceCloseModal(); // Use forceCloseModal to bypass unsaved changes check
    } catch (error: any) {
      console.error("Error updating device", error);
      
      // Check if the error is specifically for watering needs
      if (error.response?.data?.error === "Failed to update watering needs") {
        setFormError("Device updated successfully, but watering needs could not be updated. Please contact support.");
      } else {
        setFormError(`Error updating device: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await deleteDevice();
      toast.success("Device deleted successfully");
      forceCloseModal();
      navigate("/devices");
    } catch (error) {
      console.error("Error deleting device:", error);
      toast.error("Failed to delete device. Please try again.");
    }
  };

  const handleAddNewLocation = () => {
    if (hasUnsavedChanges) {
      setPendingLocationNavigation(true);
      setShowUnsavedWarning(true);
    } else {
      forceCloseModal();
      navigate("/locations");
    }
  };

  const handleDiscardChanges = () => {
    if (pendingLocationNavigation) {
      forceCloseModal();
      navigate("/locations");
    } else {
      forceCloseModal();
    }
    setShowUnsavedWarning(false);
    setPendingLocationNavigation(false);
  };

  return (
    <>
      <Modal title="Edit Device" onClose={closeModal} size={MODAL_SIZES.MEDIUM}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* <pre>{JSON.stringify(device, null, 2)}</pre> */}
          {formError && (
            <div className="text-red-600 bg-red-100 border border-red-400 rounded-md p-2 mb-4">
              {formError}
            </div>
          )}
          <div className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
            <label htmlFor="name" className="block mb-2">Name</label>
            <input
              id="name"
              className="w-full text-base md:text-sm text-gray-900 bg-gray-100 rounded-md px-3 py-2"
              {...register("name")}
              placeholder="Steve's Office Plant"
            />
          </div>
          <small className="text-gray-300">Device ID: {selectedDevice.device_id}</small>

          <div className="block text-left text-xs font-bold text-gray-700 tracking-wider mt-6 mb-8">
            <label htmlFor="location_id" className="block mb-2 uppercase">Location</label>
            <Controller
              name="location_id"
              control={control}
              render={({ field }) => (
                <LocationSelector
                  value={field.value || device?.location_id || null}
                  onChange={(locationObject) => {
                    field.onChange(locationObject?._id);
                  }}
                  onAddNewLocation={handleAddNewLocation}
                />
              )}
            />
          </div>
          <div className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-8">
            <label htmlFor="notes" className="block mb-2">Device Notes</label>
            <input
              id="notes"
              className="w-full text-base md:text-sm text-gray-900 bg-gray-100 rounded-md px-3 py-2"
              {...register("notes")}
              placeholder="This device is used for..."
            />
          </div>

          <div className="mb-3">
            <WateringNeedsSlider
              register={register}
              setValue={setValue}
              watch={watch}
              defaultValue={device?.nuf_watering_need_set || 5}
            />
          </div>

          {/* DevicePhotos component temporarily hidden to prevent auto-opening analysis modal
          <DevicePhotos
            photos={photos}
            setPhotos={handlePhotosChange}
            labelStyle="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-2"
            standalone={false}
          />
          */}

          <div className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-8">
            <label htmlFor="color" className="block mb-2">Device Color</label>
            <input
              id="color"
              type="color"
              className="w-full text-base md:text-sm text-gray-900 bg-gray-100 rounded-md px-3 py-2 min-h-10"
              {...register("color")}
            />
          </div>

          <div className="mt-6 mb-4">
            <button
              type="button"
              onClick={() => setShowDeleteConfirmation(true)}
              className="text-red-600 hover:text-red-700 text-sm font-medium underline"
            >
              Delete Device
            </button>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200 gap-8">
            <button
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring px-4 py-3 hover:bg-slate-200 hover-text-slate-600"
              type="button"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800 disabled:pointer-events-none disabled:opacity-50 disabled:bg-slate-300 disabled:text-slate-600 flex items-center gap-2"
              type="submit"
              disabled={isSaving}
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Are you sure?</h3>
            <p className="text-gray-600 mb-6">
              Unsaved changes will be lost if you close this modal.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowUnsavedWarning(false);
                  setPendingLocationNavigation(false);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Stay
              </button>
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteDevice}
        title="Delete Device"
        message={`Are you sure you want to delete "${device?.name || 'this device'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default EditDeviceModal;
